import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  ArrowDownLeft,
  CalendarClock,
  History,
  Phone,
  MessageSquare,
  Loader2,
  Ban,
  RefreshCw,
  ChevronDown,
  X,
  Clock,
  Check,
  CheckCheck,
  AlertCircle,
  List,
  Layers,
  ExternalLink,
  Copy,
  ClipboardCopy,
  Plus,
  Table2,
  Bell,
  PanelLeftClose,
  PanelLeftOpen,
  MessageCircle,
  Radio,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime, cleanSenderLabel } from '@/lib/utils/format';
import { useModuleStore } from '@/stores/module-store';
import { useConversationStore } from '@/stores/conversation-store';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { SenderPicker } from '@/components/messaging/sender-picker';
import { RecipientPicker } from '@/components/messaging/recipient-picker';
import { MessageInput } from '@/components/messaging/message-input';
import { SchedulePickerPopover } from '@/components/messaging/schedule-picker';
import { ComposeOptOutNotice } from '@/components/messaging/compose-optout-notice';
import { SendPreviewDialog } from '@/components/messaging/send-preview-dialog';
import { useMessageStore, findOptedOutRecipientPhones } from '@/stores/message-store';
import { useSettingsStore } from '@/stores/settings-store';
import { formatScheduleDate } from '@/lib/utils/schedule';
import { useSenders } from '@/hooks/use-senders';
import type { Message, Conversation, Channel, MessageHistoryFilters, Sender } from '@/types/messaging';

interface InboxPanelProps {
  view: 'scheduled' | 'history';
  onBack: () => void;
  onOpenConversation: (conv: Conversation) => void;
  onScheduleNew?: () => void;
}

interface MessageGroup {
  contactNumber: string;
  channel: Channel;
  messages: Message[];
}

/** Group consecutive messages that share the same contact number */
function groupSequentialMessages(messages: Message[]): MessageGroup[] {
  const groups: MessageGroup[] = [];
  for (const msg of messages) {
    const contact = msg.direction === 'outbound' ? msg.to : msg.from;
    const last = groups[groups.length - 1];
    if (last && last.contactNumber === contact) {
      last.messages.push(msg);
    } else {
      groups.push({ contactNumber: contact, channel: msg.channel, messages: [msg] });
    }
  }
  return groups;
}

const CHANNEL_ICON: Record<Channel, React.ComponentType<{ className?: string }>> = {
  SMS: MessageSquare,
  MMS: MessageSquare,
  WHATSAPP: WhatsAppIcon,
  VOICE: Radio,
  EMAIL: Mail,
  INTERCOM: MessageCircle,
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'sending', label: 'Sending' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed', label: 'Failed' },
  { value: 'undelivered', label: 'Undelivered' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'canceled', label: 'Canceled' },
];

const DIRECTION_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
];

const CHANNEL_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'SMS', label: 'SMS' },
  { value: 'MMS', label: 'MMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
];

export function InboxPanel({ view, onBack, onOpenConversation, onScheduleNew }: InboxPanelProps) {
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const cancelScheduledMessage = useConversationStore((s) => s.cancelScheduledMessage);

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Inline compose for scheduling
  const [showCompose, setShowCompose] = useState(false);
  const msgSender = useMessageStore((s) => s.sender);
  const msgRecipients = useMessageStore((s) => s.recipients);
  const msgBody = useMessageStore((s) => s.body);
  const msgChannel = useMessageStore((s) => s.channel);
  const msgSending = useMessageStore((s) => s.sending);
  const msgSendError = useMessageStore((s) => s.sendError);
  const msgLastResult = useMessageStore((s) => s.lastResult);
  const msgSend = useMessageStore((s) => s.send);
  const msgScheduledAt = useMessageStore((s) => s.scheduledAt);
  const msgSetScheduledAt = useMessageStore((s) => s.setScheduledAt);
  const msgReset = useMessageStore((s) => s.reset);
  const msgSelectedTemplate = useMessageStore((s) => s.selectedTemplate);
  const msgTemplateVariables = useMessageStore((s) => s.templateVariables);

  const hasUnfilledVars = msgSelectedTemplate?.variables && msgSelectedTemplate.variables.length > 0
    && msgSelectedTemplate.variables.some((v: string) => !msgTemplateVariables[v]?.trim());
  // KAN-155: several recipients and some of them opted out — the same preview
  // Compose shows asks first ("N opted out" + "Send to them anyway").
  const [showOptOutPreview, setShowOptOutPreview] = useState(false);
  const [optedOutPhones, setOptedOutPhones] = useState<string[]>([]);
  // True while the opt-out list is read — the button shows a spinner.
  const [checkingOptOut, setCheckingOptOut] = useState(false);
  const scheduleDisabled = msgSending || checkingOptOut || !msgSender || msgRecipients.length === 0 || !msgBody.trim() || !msgScheduledAt || !!hasUnfilledVars;

  const handleScheduleSend = async (options?: { optOutSendAnyway?: boolean; optOutSkipPhones?: string[] }) => {
    if (!msgScheduledAt) return;
    await msgSend(options);
    // After success, refresh the list and go back
    if (useMessageStore.getState().lastResult?.success) {
      setShowCompose(false);
      fetchData();
    }
  };

  /** Schedule button. One recipient, or nobody opted out: schedule right away,
   *  as before. Several recipients with opted-out ones: open the preview. */
  const handleScheduleClick = async () => {
    if (msgRecipients.length <= 1) {
      await handleScheduleSend();
      return;
    }
    // One read of the sender's opt-out list, none while opt-out is off.
    let phones: string[] = [];
    setCheckingOptOut(true);
    try {
      phones = await findOptedOutRecipientPhones();
    } finally {
      setCheckingOptOut(false);
    }
    if (phones.length > 0) {
      setOptedOutPhones(phones);
      setShowOptOutPreview(true);
      return;
    }
    // Already checked — nobody to skip, and the list is not read again.
    await handleScheduleSend({ optOutSkipPhones: [] });
  };

  /** Confirmed from the preview — keep the recipients left in it and schedule. */
  const handleOptOutPreviewConfirm = async (recipientIds: string[], sendToOptedOut: boolean) => {
    setShowOptOutPreview(false);
    if (recipientIds.length < msgRecipients.length) {
      const confirmedSet = new Set(recipientIds);
      useMessageStore.setState({ recipients: msgRecipients.filter((r) => confirmedSet.has(r.id)) });
    }
    await handleScheduleSend({ optOutSkipPhones: sendToOptedOut ? [] : optedOutPhones });
  };
  // Signature + country code for that preview — read as the Compose send button does.
  const schedulePrefs = useSettingsStore.getState().preferences;

  const handleOpenCompose = () => {
    msgReset();
    // Pre-set a default schedule (e.g. 1 hour from now) so the user knows it's schedule-only
    msgSetScheduledAt(new Date(Date.now() + 60 * 60 * 1000));
    setShowCompose(true);
  };

  const handleCloseCompose = () => {
    setShowCompose(false);
  };

  // View mode
  const [viewMode, setViewMode] = useState<'grouped' | 'list' | 'table'>('grouped');

  // Senders for filter dropdown (uses cached getSenders — no duplicate API calls)
  const { senders } = useSenders('inbox');

  // Scheduled view filter
  const [scheduledFilter, setScheduledFilter] = useState<'all' | 'scheduled' | 'sent' | 'canceled'>('scheduled');

  // Filters (history view only)
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterSender, setFilterSender] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const activeFilterCount = [filterStatus, filterDirection, filterChannel, filterSender, filterDateFrom, filterDateTo].filter(Boolean).length;

  const senderOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [{ value: '', label: 'All' }];
    for (const s of senders) {
      opts.push({ value: s.phoneNumber || s.id, label: cleanSenderLabel(s.label) || s.phoneNumber || s.id });
    }
    return opts;
  }, [senders]);

  const buildFilters = useCallback((): MessageHistoryFilters | undefined => {
    if (!filterStatus && !filterDirection && !filterChannel && !filterSender && !filterDateFrom && !filterDateTo) return undefined;
    return {
      ...(filterStatus && { status: filterStatus }),
      ...(filterDirection && { direction: filterDirection }),
      ...(filterChannel && { channel: filterChannel }),
      ...(filterSender && { from: filterSender }),
      ...(filterDateFrom && { dateFrom: filterDateFrom }),
      ...(filterDateTo && { dateTo: filterDateTo }),
    };
  }, [filterStatus, filterDirection, filterChannel, filterSender, filterDateFrom, filterDateTo]);

  const fetchData = useCallback(async () => {
    if (!tpaModule) return;
    setLoading(true);
    try {
      if (view === 'scheduled' && tpaModule.getScheduledMessages) {
        const msgs = await tpaModule.getScheduledMessages();
        setMessages(msgs);
      } else if (view === 'history' && tpaModule.getMessageHistory) {
        const filters = buildFilters();
        const result = await tpaModule.getMessageHistory(null, filters);
        setMessages(result.messages);
        setHistoryCursor(result.nextCursor);
      }
    } catch (err) {
      console.warn('[InboxPanel] Error fetching:', err);
    } finally {
      setLoading(false);
    }
  }, [tpaModule, view, buildFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLoadMore = async () => {
    if (!tpaModule?.getMessageHistory || !historyCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await tpaModule.getMessageHistory(historyCursor);
      setMessages((prev) => [...prev, ...result.messages]);
      setHistoryCursor(result.nextCursor);
    } catch (err) {
      console.warn('[InboxPanel] Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCancel = async (msgId: string) => {
    setCancellingId(msgId);
    const result = await cancelScheduledMessage(msgId);
    if (result.success) {
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
    }
    setCancellingId(null);
  };

  const handleClickMessage = (msg: Message) => {
    const conv: Conversation = {
      id: msg.conversationId,
      contactName: msg.direction === 'outbound' ? msg.to : msg.from,
      contactPhone: (msg.direction === 'outbound' ? msg.to : msg.from).replace(/\+|whatsapp:/g, ''),
      lastMessage: msg.body,
      lastMessageTime: new Date(msg.timestamp),
      unreadCount: 0,
      channel: msg.channel,
    };
    onOpenConversation(conv);
  };

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterDirection('');
    setFilterChannel('');
    setFilterSender('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const isScheduled = view === 'scheduled';
  const title = isScheduled ? 'Scheduled Messages' : 'Messages History';
  const TitleIcon = isScheduled ? CalendarClock : History;

  // Apply scheduled view filter
  const filteredMessages = useMemo(() => {
    if (!isScheduled || scheduledFilter === 'all') return messages;
    return messages.filter((msg) => {
      const raw = (msg.metadata?.rawStatus as string) || msg.status;
      if (scheduledFilter === 'sent') return raw === 'sent';
      if (scheduledFilter === 'canceled') return raw === 'canceled';
      if (scheduledFilter === 'scheduled') return raw === 'scheduled';
      return true;
    });
  }, [messages, isScheduled, scheduledFilter]);

  const grouped = useMemo(() => groupSequentialMessages(filteredMessages), [filteredMessages]);

  return (
    <div data-testid={isScheduled ? 'scheduled-messages-panel' : 'messages-history-panel'} className="h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
        <button
          onClick={onBack}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <TitleIcon className={cn('h-5 w-5', isScheduled ? 'text-primary/60' : 'text-muted-foreground')} />
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <div className="ml-auto flex items-center gap-1">
          {isScheduled && !showCompose && (
            <button
              onClick={handleOpenCompose}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors mr-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Schedule
            </button>
          )}
          {!isScheduled && (
            <div className="flex items-center rounded-full border border-border p-0.5">
              <button
                onClick={() => setViewMode('grouped')}
                className={cn(
                  'flex items-center justify-center w-6 h-6 rounded-full transition-colors',
                  viewMode === 'grouped'
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                title="Grouped view"
              >
                <Layers className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'flex items-center justify-center w-6 h-6 rounded-full transition-colors',
                  viewMode === 'list'
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                title="List view"
              >
                <List className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={cn(
                  'flex items-center justify-center w-6 h-6 rounded-full transition-colors',
                  viewMode === 'table'
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                title="Table view"
              >
                <Table2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <button
            onClick={fetchData}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Filter bar — scheduled view */}
      {isScheduled && !showCompose && (
        <div className="px-4 py-2 border-b border-border shrink-0">
          <div className="flex items-center gap-1.5">
            {(['all', 'scheduled', 'sent', 'canceled'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setScheduledFilter(f)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                  scheduledFilter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                {f === 'all' ? 'All' : f === 'sent' ? 'Processed' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filter bar — always visible for history */}
      {!isScheduled && (
        <div className="px-4 py-2.5 border-b border-border shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <FilterSelect
              label="Status"
              value={filterStatus}
              onChange={setFilterStatus}
              options={STATUS_OPTIONS}
            />
            <FilterSelect
              label="Direction"
              value={filterDirection}
              onChange={setFilterDirection}
              options={DIRECTION_OPTIONS}
            />
            <FilterSelect
              label="Channel"
              value={filterChannel}
              onChange={setFilterChannel}
              options={CHANNEL_OPTIONS}
            />
            {senderOptions.length > 1 && (
              <FilterSelect
                label="Sender"
                value={filterSender}
                onChange={setFilterSender}
                options={senderOptions}
              />
            )}
            <FilterDate
              label="Sent after"
              value={filterDateFrom}
              onChange={setFilterDateFrom}
            />
            <FilterDate
              label="Sent before"
              value={filterDateTo}
              onChange={setFilterDateTo}
            />
            {activeFilterCount > 0 && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div data-testid={isScheduled ? 'scheduled-messages-list' : 'messages-history-list'} className="flex-1 overflow-y-auto min-h-0">
        {showCompose && isScheduled ? (
          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">Schedule a message</h3>
              <button
                onClick={handleCloseCompose}
                className="flex items-center justify-center w-6 h-6 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <SenderPicker />
            <RecipientPicker />
            <MessageInput />
            <div className="border-t border-border pt-4">
              {/* Schedule picker + send */}
              {msgSendError && (
                <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive mb-3">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <p className="text-xs">{msgSendError}</p>
                </div>
              )}
              {/* KAN-155: the one recipient opted out — held back until the agent picks "Send anyway" */}
              <ComposeOptOutNotice
                className="mb-3"
                onSendAnyway={() => { void handleScheduleSend({ optOutSendAnyway: true }); }}
              />
              {/* KAN-155: several recipients, some opted out — same preview as Compose */}
              <SendPreviewDialog
                open={showOptOutPreview}
                onClose={() => setShowOptOutPreview(false)}
                onConfirmSend={handleOptOutPreviewConfirm}
                recipients={msgRecipients}
                optedOutPhones={optedOutPhones}
                body={msgSelectedTemplate ? Object.entries(msgTemplateVariables).reduce((b, [k, v]) => b.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v || `{{${k}}}`), msgBody) : msgBody}
                signature={schedulePrefs.messageSignature?.trim()}
                hasPlaceholders={/\{\{[\w-]+\}\}/.test(msgBody)}
                hasProperties={msgRecipients.some((r) => r.properties && Object.keys(r.properties).length > 0)}
                isTemplate={false}
                countryCode={schedulePrefs.defaultCountryCode?.replace(/^\+/, '')}
                isPhoneBased={tpaModule?.isPhoneBasedRecipient?.() ?? true}
              />
              {msgLastResult?.success && (
                <div className="flex items-start gap-2 rounded-lg bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-700 dark:text-green-400 mb-3">
                  <CheckCheck className="h-4 w-4 shrink-0 mt-0.5" />
                  <p className="text-xs font-medium">Message scheduled!</p>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="flex items-stretch">
                  <button
                    onClick={() => { void handleScheduleClick(); }}
                    disabled={scheduleDisabled}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-2 rounded-l-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50',
                      (msgSending || checkingOptOut) && 'animate-pulse',
                    )}
                  >
                    {msgSending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Scheduling...
                      </>
                    ) : checkingOptOut ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Checking recipients...
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4" />
                        Schedule {msgChannel}
                        {msgScheduledAt && (
                          <span className="ml-1 text-[10px] bg-primary-foreground/20 px-1.5 py-0.5 rounded-full font-normal">
                            {formatScheduleDate(msgScheduledAt)}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                  <SchedulePickerPopover
                    scheduledAt={msgScheduledAt}
                    onSchedule={msgSetScheduledAt}
                    align="end"
                    side="top"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            {isScheduled ? (
              <>
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mb-4">
                  <CalendarClock className="h-7 w-7 text-primary/60" />
                </div>
                <p className="text-sm font-medium text-foreground mb-1">No scheduled messages</p>
                <p className="text-xs text-muted-foreground mb-4">Schedule messages to be sent at a later time</p>
                <button
                  onClick={handleOpenCompose}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Schedule a message
                </button>
              </>
            ) : (
              <>
                <TitleIcon className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  {activeFilterCount > 0
                    ? 'No messages match your filters'
                    : 'No message history found'
                  }
                </p>
              </>
            )}
          </div>
        ) : (
          <div>
            {viewMode === 'grouped' ? (
              // Grouped view
              grouped.map((group, gi) => {
                const Icon = CHANNEL_ICON[group.channel] || Phone;
                const isSingle = group.messages.length === 1;

                if (isSingle) {
                  const msg = group.messages[0];
                  return (
                    <MessageRow
                      key={msg.id}
                      message={msg}
                      isScheduledView={isScheduled}
                      isCancelling={cancellingId === msg.id}
                      onCancel={() => handleCancel(msg.id)}
                      onOpenConversation={() => handleClickMessage(msg)}
                      senders={senders}
                      showAvatar
                    />
                  );
                }

                return (
                  <div key={`g-${gi}`} className="border-b border-border">
                    {/* Group header */}
                    <div
                      onClick={() => handleClickMessage(group.messages[0])}
                      role="button"
                      tabIndex={0}
                      className="flex items-center gap-3 px-4 py-1.5 hover:bg-accent/50 cursor-pointer transition-colors"
                    >
                      <div className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                        group.channel === 'WHATSAPP' ? 'bg-green-500/10' : 'bg-blue-500/10'
                      )}>
                        <Icon className={cn(
                          'h-4 w-4',
                          group.channel === 'WHATSAPP' ? 'text-green-500' : 'text-blue-500'
                        )} />
                      </div>
                      <span className="text-sm font-medium text-foreground truncate">
                        {group.contactNumber}
                      </span>
                      <span className="ml-auto text-[11px] text-muted-foreground bg-muted rounded-full px-2 py-0.5 shrink-0">
                        {group.messages.length} messages
                      </span>
                    </div>
                    {/* Grouped messages — compact, no avatar */}
                    {group.messages.map((msg) => (
                      <MessageRow
                        key={msg.id}
                        message={msg}
                        isScheduledView={isScheduled}
                        isCancelling={cancellingId === msg.id}
                        onCancel={() => handleCancel(msg.id)}
                        onOpenConversation={() => handleClickMessage(msg)}
                        senders={senders}
                        showAvatar={false}
                      />
                    ))}
                  </div>
                );
              })
            ) : viewMode === 'table' ? (
              // Table view
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Channel</th>
                      <th className="px-3 py-2 font-medium">Direction</th>
                      <th className="px-3 py-2 font-medium">Contact</th>
                      <th className="px-3 py-2 font-medium">Sender</th>
                      <th className="px-3 py-2 font-medium">Message</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium whitespace-nowrap">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMessages.map((msg) => {
                      const msgIsOutbound = msg.direction === 'outbound';
                      const msgContact = msgIsOutbound ? msg.to : msg.from;
                      const msgSenderNum = msgIsOutbound ? msg.from : msg.to;
                      const msgMatchedSender = senders.find(
                        (s) => s.phoneNumber === msgSenderNum || s.id === msgSenderNum
                      );
                      const msgFriendly = cleanSenderLabel(msgMatchedSender?.label || '');
                      const MsgIcon = CHANNEL_ICON[msg.channel] || Phone;
                      const msgRawStatus = (msg.metadata?.rawStatus as string) || msg.status;

                      return (
                        <tr
                          key={msg.id}
                          onClick={() => handleClickMessage(msg)}
                          className="border-b border-border hover:bg-accent cursor-pointer transition-colors"
                        >
                          <td className="px-3 py-2">
                            <MsgIcon className={cn(
                              'h-3.5 w-3.5',
                              msg.channel === 'WHATSAPP' ? 'text-green-500' : 'text-blue-500'
                            )} />
                          </td>
                          <td className="px-3 py-2">
                            <span className={cn(
                              'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                              msgIsOutbound ? 'bg-orange-500/10 text-orange-600' : 'bg-blue-500/10 text-blue-600'
                            )}>
                              {msgIsOutbound ? 'out' : 'in'}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium text-foreground truncate max-w-[120px]">
                            {msgContact}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[100px]">
                            {msgFriendly || msgSenderNum}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px]">
                            {msg.body || '(no text)'}
                          </td>
                          <td className="px-3 py-2">
                            <StatusPill status={msgRawStatus} isInbound={!msgIsOutbound} />
                          </td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                            {formatRelativeTime(new Date(msg.timestamp))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              // List view — flat, every message with avatar
              filteredMessages.map((msg) => (
                <MessageRow
                  key={msg.id}
                  message={msg}
                  isScheduledView={isScheduled}
                  isCancelling={cancellingId === msg.id}
                  onCancel={() => handleCancel(msg.id)}
                  onOpenConversation={() => handleClickMessage(msg)}
                  senders={senders}
                  showAvatar
                />
              ))
            )}
            {view === 'history' && historyCursor && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors border-t border-border"
              >
                {loadingMore ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                {loadingMore ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Compact filter controls ──────────────────────────────────────── */

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const isActive = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-7 rounded-full border px-2.5 pr-6 text-xs appearance-none bg-no-repeat cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-ring',
        isActive
          ? 'border-primary/40 bg-primary/5 text-foreground'
          : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-border'
      )}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
        backgroundPosition: 'right 6px center',
      }}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.value === '' ? label : opt.label}
        </option>
      ))}
    </select>
  );
}

function FilterDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const isActive = !!value;
  return (
    <div className="relative">
      {!value && (
        <span className="absolute inset-0 flex items-center pl-2.5 text-xs text-muted-foreground pointer-events-none">
          {label}
        </span>
      )}
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-7 rounded-full border px-2.5 text-xs cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-ring',
          isActive
            ? 'border-primary/40 bg-primary/5 text-foreground'
            : 'border-border bg-background text-transparent hover:border-border',
        )}
      />
    </div>
  );
}

/* ── Message row ──────────────────────────────────────────────────── */

const STATUS_PILL_STYLES: Record<string, string> = {
  queued: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
  sending: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
  scheduled: 'bg-primary/10 text-primary',
  sent: 'bg-muted text-muted-foreground',
  delivered: 'bg-green-500/10 text-green-700 dark:text-green-400',
  read: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  failed: 'bg-destructive/10 text-destructive',
  undelivered: 'bg-destructive/10 text-destructive',
  canceled: 'bg-muted text-muted-foreground',
  received: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
};

function StatusPill({ status, isInbound }: { status: string; isInbound: boolean }) {
  const label = isInbound ? 'received' : status;
  const style = STATUS_PILL_STYLES[label] || 'bg-muted text-muted-foreground';
  return (
    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize shrink-0', style)}>
      {label}
    </span>
  );
}

function MessageStatusIcon({ status, isInbound }: { status: string; isInbound: boolean }) {
  if (isInbound) {
    return <ArrowDownLeft className="h-3.5 w-3.5 text-blue-500 shrink-0" />;
  }
  switch (status) {
    case 'queued':
    case 'sending':
      return <Clock className="h-3.5 w-3.5 text-yellow-600 shrink-0" />;
    case 'scheduled':
      return <CalendarClock className="h-3.5 w-3.5 text-primary/60 shrink-0" />;
    case 'sent':
      return <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />;
    case 'delivered':
      return <CheckCheck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />;
    case 'read':
      return <CheckCheck className="h-3.5 w-3.5 text-blue-500 shrink-0" />;
    case 'failed':
    case 'undelivered':
      return <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />;
    case 'canceled':
      return <Ban className="h-3.5 w-3.5 text-muted-foreground shrink-0" />;
    default:
      return <Check className="h-3.5 w-3.5 text-muted-foreground shrink-0" />;
  }
}

function MessageRow({
  message,
  isScheduledView,
  isCancelling,
  onCancel,
  onOpenConversation,
  showAvatar = true,
  senders = [],
}: {
  message: Message;
  isScheduledView: boolean;
  isCancelling: boolean;
  onCancel: () => void;
  onOpenConversation: () => void;
  showAvatar?: boolean;
  senders?: Sender[];
}) {
  const Icon = CHANNEL_ICON[message.channel] || Phone;
  const isOutbound = message.direction === 'outbound';
  const isInbound = !isOutbound;
  const contactNumber = isOutbound ? message.to : message.from;
  const senderNumber = isOutbound ? message.from : message.to;
  const matchedSender = senders.find(
    (s) => s.phoneNumber === senderNumber || s.id === senderNumber
  );
  const friendlyName = cleanSenderLabel(matchedSender?.label || '');
  const scheduledAt = message.metadata?.scheduledAt as string | undefined;
  const scheduledBy = message.metadata?.scheduledBy as { uid: string; displayName: string; email: string; photoURL?: string } | undefined;
  const rawStatus = (message.metadata?.rawStatus as string) || message.status;

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [copied, setCopied] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => {
      setCopied(null);
      setMenuOpen(false);
    }, 800);
  };

  const MENU_WIDTH = 210;

  const handleRowClick = (e: React.MouseEvent) => {
    const rowRect = rowRef.current?.getBoundingClientRect();
    if (rowRect) {
      let x = e.clientX - rowRect.left;
      // Clamp so menu doesn't overflow the row's right edge
      if (x + MENU_WIDTH > rowRect.width) {
        x = rowRect.width - MENU_WIDTH;
      }
      if (x < 0) x = 0;
      setMenuPos({ x, y: e.clientY - rowRect.top });
    }
    setMenuOpen((v) => !v);
  };

  return (
    <div className="relative" ref={rowRef}>
      <div
        onClick={handleRowClick}
        role="button"
        tabIndex={0}
        className={cn(
          'flex items-start gap-3 hover:bg-accent cursor-pointer transition-colors group',
          showAvatar ? 'px-4 py-3 border-b border-border' : 'pl-16 pr-4 py-2',
          menuOpen && 'bg-accent',
        )}
      >
        {/* Channel icon — only for ungrouped messages */}
        {showAvatar && (
          <div className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full mt-0.5',
            message.channel === 'WHATSAPP' ? 'bg-green-500/10' : 'bg-blue-500/10'
          )}>
            <Icon className={cn(
              'h-4 w-4',
              message.channel === 'WHATSAPP' ? 'text-green-500' : 'text-blue-500'
            )} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {showAvatar && (
              <span className="text-sm font-medium text-foreground truncate">
                {contactNumber}
              </span>
            )}
            {friendlyName && (
              <span className="text-[11px] text-muted-foreground truncate shrink-0">
                {isOutbound ? 'from' : 'to'} {friendlyName}
              </span>
            )}
            {isScheduledView && <StatusPill status={rawStatus} isInbound={isInbound} />}
            <span className="text-xs text-muted-foreground whitespace-nowrap ml-auto shrink-0">
              {formatRelativeTime(new Date(message.timestamp))}
            </span>
          </div>
          {isScheduledView && scheduledAt && (
            <div className="flex items-center gap-1 mt-0.5">
              <CalendarClock className="h-3 w-3 text-primary/50 shrink-0" />
              <span className="text-[11px] text-primary/70">
                {rawStatus === 'sent' ? 'Sent' : 'Sends'} {formatRelativeTime(new Date(scheduledAt))}
              </span>
              {scheduledBy?.displayName && (
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                  ·
                  {scheduledBy.photoURL ? (
                    <img src={scheduledBy.photoURL} alt="" className="h-3.5 w-3.5 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-muted text-[8px] font-medium">
                      {scheduledBy.displayName.charAt(0).toUpperCase()}
                    </span>
                  )}
                  {scheduledBy.displayName}
                </span>
              )}
            </div>
          )}
          <div className={cn(
            'flex items-center gap-1.5 mt-0.5',
            rawStatus === 'canceled' && 'line-through decoration-muted-foreground/50',
          )}>
            <MessageStatusIcon status={rawStatus} isInbound={isInbound} />
            <p className="text-[13px] text-muted-foreground truncate">
              {message.body || '(no text)'}
            </p>
          </div>
        </div>

        {/* Cancel button for scheduled messages */}
        {isScheduledView && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            disabled={isCancelling}
            className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-destructive hover:bg-destructive/10 transition-colors mt-0.5 opacity-0 group-hover:opacity-100"
          >
            {isCancelling ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Ban className="h-3 w-3" />
            )}
            Cancel
          </button>
        )}
      </div>

      {/* Context menu */}
      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute z-20 min-w-[200px] rounded-lg border border-border bg-popover shadow-lg py-1 animate-in fade-in-0 zoom-in-95"
          style={{ left: menuPos.x, top: menuPos.y }}
        >
          <button
            onClick={() => { setMenuOpen(false); onOpenConversation(); }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
            Open conversation
          </button>
          <button
            onClick={() => handleCopy(contactNumber, 'number')}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
          >
            <Copy className="h-3.5 w-3.5 text-muted-foreground" />
            {copied === 'number' ? 'Copied!' : 'Copy recipient number'}
          </button>
          {message.body && (
            <button
              onClick={() => handleCopy(message.body, 'body')}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
            >
              <ClipboardCopy className="h-3.5 w-3.5 text-muted-foreground" />
              {copied === 'body' ? 'Copied!' : 'Copy message content'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
